'use client'

import { createContext, useContext } from 'react'
import type { SearchFilters } from './rank'

export type { SearchFilters }

export type IndexingStatus = 'idle' | 'indexing' | 'complete'

export interface EmbeddingContextValue {
  /** Model loaded and ready for inference */
  modelReady: boolean
  /** Model currently downloading */
  modelLoading: boolean
  /** Download progress 0-100 */
  modelProgress: number
  /** Current indexing status */
  indexingStatus: IndexingStatus
  /** How many entries are indexed */
  indexedCount: number
  /** Total entries to index */
  totalCount: number
  /** Embed texts into vectors */
  embed: (texts: string[]) => Promise<number[][]>
  /** Search for similar entries */
  search: (
    query: string,
    topK?: number,
    filters?: SearchFilters,
  ) => Promise<{ entryId: string; score: number; cosineScore: number }[]>
  /** Find entries similar to a given entry */
  findSimilarEntries: (
    entryId: string,
    topK?: number,
  ) => Promise<{ entryId: string; score: number; cosineScore: number }[]>
}

export const EmbeddingContext = createContext<EmbeddingContextValue | null>(
  null,
)

/**
 * Access the embedding context for semantic search.
 * Returns null if the provider is not mounted — search is optional.
 */
export function useEmbeddingContext(): EmbeddingContextValue | null {
  return useContext(EmbeddingContext)
}
